import CheckBox from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank';
import IndeterminateCheckBox from '@mui/icons-material/IndeterminateCheckBox';
import { alpha, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';

export type SelectAllState = 'none' | 'some' | 'all';

interface SelectAllOptionProps {
  isDisabled?: boolean;
  label: string;
  onClick: () => void;
  state: SelectAllState;
}

/**
 * "Select all" row pinned above multi-select options. Clicking selects every option
 * from none or some, and clears them all from all.
 */
export default function SelectAllOption({
  isDisabled = false,
  label,
  onClick,
  state,
}: SelectAllOptionProps) {
  const theme = useTheme();

  return (
    <Box
      aria-disabled={isDisabled}
      onClick={isDisabled ? undefined : onClick}
      role="button"
      sx={{
        alignItems: 'center',
        cursor: isDisabled ? 'default' : 'pointer',
        display: 'flex',
        gap: 1,
        // matches the option rows below it: 48px rows, MUI's 16px option padding,
        // and the 3px left border option rows reserve for their focus highlight
        borderLeft: '3px solid transparent',
        minHeight: '48px',
        opacity: isDisabled ? 0.5 : 1,
        px: 2,
        ...(!isDisabled && {
          '&:hover': {
            backgroundColor: alpha(theme.brandV2.colors.treadYellow, 0.15),
            borderLeftColor: theme.brandV2.colors.treadYellow,
          },
        }),
      }}
    >
      <Checkbox
        checked={state === 'all'}
        checkedIcon={<CheckBox fontSize="small" />}
        disabled={isDisabled}
        icon={<CheckBoxOutlineBlank fontSize="small" />}
        indeterminate={state === 'some'}
        indeterminateIcon={<IndeterminateCheckBox fontSize="small" />}
        style={{ marginRight: '8px', padding: 0 }}
        tabIndex={-1}
      />
      <Typography sx={{ fontWeight: 700 }} variant="body2">
        {label}
      </Typography>
    </Box>
  );
}
